import { Injectable } from '@angular/core';
import { ApplicationHttpClient } from '@app/helpers/custom-http-client';
import { HttpParams } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpResult } from '@app/shared/models/http-result';
import {
  BrandFacebookIntegration,
  BrandInstagramIntegration,
  BrandIntegrationData,
  BrandTwitterIntegration,
} from '@app/shared/models/integration';
import { FilterRequest } from '../models/filter';

const TWITTER_CONNECT_REDIRECT_URL_HANDLER =
  'brands/integrations/twitter-connect-handler';
const FACEBOOK_CONNECT_REDIRECT_URL_HANDLER =
  'brands/integrations/facebook-connect-handler';

@Injectable({
  providedIn: 'root',
})
export class IntegrationsService {
  private twitterIntegrationURL = '/integrations/social-media/twitter';
  private googleIntegrationURL = '/integrations';
  private facebookIntegrationURL = '/integrations/social-media/facebook';
  private instagramIntegrationURL = '/integrations/social-media/instagram';

  constructor(private http: ApplicationHttpClient) {}

  requestGoogleOAuthUrl(redirectPath: string): Observable<string> {
    const googleOAuthUrl = this.googleIntegrationURL + '/google-oauth-url';
    return this.http
      .get<HttpResult>(googleOAuthUrl, {
        params: new HttpParams().set(
          'redirectURL',
          `${window.location.origin}/${redirectPath}`
        ),
      })
      .pipe(
        map((it: HttpResult) => {
          return it.data;
        })
      );
  }

  submitGoogleCode(code: string, brandId: string): Observable<void> {
    const submitCodeUrl =
      this.googleIntegrationURL + '/submit-code?withLocations=false';
    return this.http.post(submitCodeUrl, {
      brandId: brandId,
      code: code,
    });
  }

  requestTwitterOAuthUrl(): Observable<string> {
    const twitterOAuthUrl = this.twitterIntegrationURL + '/authorization-url';
    return this.http
      .get<HttpResult>(twitterOAuthUrl, {
        params: new HttpParams().set(
          'redirect_url',
          `${window.location.origin}/${TWITTER_CONNECT_REDIRECT_URL_HANDLER}`
        ),
      })
      .pipe(map((it: HttpResult) => it.data));
  }

  submitTwitterCode(
    code: string,
    codeVerifier: string,
    brandId: string
  ): Observable<void> {
    const submitCodeUrl = this.twitterIntegrationURL + '/submit-brand-auth';
    const query = `?brand_id=${brandId}&code_verifier=${codeVerifier}&code=${code}`;
    return this.http.get(submitCodeUrl + query);
  }

  disconnectTwitter(brandId: string): Observable<void> {
    const disconnectTwitterUrl =
      this.twitterIntegrationURL + '/disconnect/' + brandId;
    return this.http.delete(disconnectTwitterUrl);
  }

  public getMyBrandsWithTwitter(): Observable<BrandTwitterIntegration[]> {
    const getMyBrandsUrl = this.twitterIntegrationURL + '/me/brands';
    return this.http.get<HttpResult>(getMyBrandsUrl).pipe(map((it) => it.data));
  }

  // ====== Facebook Section =========
  requestFacebookOAuthUrl(): Observable<string> {
    const facebookOAuthUrl = this.facebookIntegrationURL + '/authorization-url';
    return this.http
      .get<HttpResult>(facebookOAuthUrl, {
        params: new HttpParams().set(
          'redirect_url',
          `${window.location.origin}/${FACEBOOK_CONNECT_REDIRECT_URL_HANDLER}`
        ),
      })
      .pipe(map((it: HttpResult) => it.data));
  }

  submitFacebookCode(
    code: string,
    codeVerifier: string,
    brandId: string
  ): Observable<void> {
    const submitCodeUrl = this.facebookIntegrationURL + '/submit-brand-auth';
    const query = `?brand_id=${brandId}&code_verifier=${codeVerifier}&code=${code}`;
    return this.http.get(submitCodeUrl + query);
  }

  disconnectFacebook(brandId: string): Observable<void> {
    const disconnectFacebookUrl =
      this.facebookIntegrationURL + '/disconnect/' + brandId;
    return this.http.delete(disconnectFacebookUrl);
  }

  public getMyBrandsWithFacebook(): Observable<BrandFacebookIntegration[]> {
    const getMyBrandsUrl = this.facebookIntegrationURL + '/me/brands';
    return this.http.get<HttpResult>(getMyBrandsUrl).pipe(map((it) => it.data));
  }

  public getMyBrandsWithInstagram(): Observable<BrandInstagramIntegration[]> {
    const getMyBrandsUrl = this.instagramIntegrationURL + '/me/brands';
    return this.http.get<HttpResult>(getMyBrandsUrl).pipe(map((it) => it.data));
  }

  public filterFacebookPages(
    filters: FilterRequest[]
  ): Observable<BrandFacebookIntegration[]> {
    return this.http
      .get<HttpResult>(
        `/integrations/social-media/facebook/me/brands?filter=${JSON.stringify(
          filters
        )}`
      )
      .pipe(map((it: HttpResult) => it.data));
  }

  public filterInstagramPages(
    filters: FilterRequest[]
  ): Observable<BrandInstagramIntegration[]> {
    return this.http
      .get<HttpResult>(
        `/integrations/social-media/instagram/me/brands?filter=${JSON.stringify(
          filters
        )}`
      )
      .pipe(map((it: HttpResult) => it.data));
  }

  public getIntegrationByType(
    activeIntegrationsIds: string[] = []
  ): Observable<BrandIntegrationData> {
    return forkJoin([
      this.getMyBrandsWithFacebook(),
      this.getMyBrandsWithTwitter(),
      this.getMyBrandsWithInstagram(),
    ]).pipe(
      map(([allFacebookBrands, allTwitterBrands, allInstagramBrands]) => {
        const activeFacebookIds = allFacebookBrands
          .filter((brand) => activeIntegrationsIds.includes(brand.id))
          .map((brand) => brand.id);

        const activeTwitterIds = allTwitterBrands
          .filter((brand) => activeIntegrationsIds.includes(brand.id))
          .map((brand) => brand.id);

        const activeInstagramIds = allInstagramBrands
          .filter((brand) => activeIntegrationsIds.includes(brand.id))
          .map((brand) => brand.id);

        return {
          allFacebookBrands,
          allTwitterBrands,
          allInstagramBrands,
          activeFacebookIds,
          activeTwitterIds,
          activeInstagramIds,
        };
      })
    );
  }
}
